import { useEffect, useMemo, useState } from "react";
import CarouselRail from "@/components/yts/CarouselRail";
import ExperienceCard from "@/components/yts/ExperienceCard";
import { useExplore, toCard } from "@/lib/ExploreContext";
import { useLang } from "@/lib/i18n";
import { base44 } from "@/api/base44Client";
import { normalizeViator } from "@/lib/viator";
import { withNetworkRetry } from "@/lib/requestRetry";

// A single cultural edit, not a grab-bag of general tours. Bomba and salsa
// have their own traditions; editorial descriptions make that distinction clear.
// The item IDs come from real, published records in the partner catalogs.
const PICKS = [
  // Lead with the two colorful We Take You Bomba photographs supplied for the site.
  { provider: "fareharbor", id: "742786", rhythm: "Bomba", en: "Discover Loíza's culture and art, followed by a bomba dance class.", es: "Conoce la cultura y el arte de Loíza y participa en una clase de bomba." },
  { provider: "fareharbor", id: "742785", rhythm: "Bomba", en: "Learn the steps and rhythms of bomba in Loíza with live drums.", es: "Aprende los pasos de la bomba en Loíza al ritmo de tambores en vivo." },
  { provider: "viator", id: "6ab96869c558ac91c41118a9", rhythm: "Bomba", en: "An Afro-Puerto Rican bomba class accompanied by live music.", es: "Aprende bomba afropuertorriqueña con música en vivo." },
  { provider: "viator", id: "6ab9686aaad6f4af94d29a23", rhythm: "Salsa", en: "Join a salsa class in San Juan with local dance instructors.", es: "Toma una clase de salsa en San Juan con instructores locales." },
  { provider: "fareharbor", id: "457277", rhythm: "Salsa", en: "Practice salsa at the beach around sunset.", es: "Baila salsa en la playa al caer la tarde." },
  { provider: "viator", id: "6ab9686b53b66064e5dd3d33", rhythm: "Salsa", en: "A salsa class and San Juan nightlife experience.", es: "Combina una clase de salsa con la vida nocturna de San Juan." },
];

const fareHarborId = (listing) =>
  String(listing?.booking_url || listing?.primary_action_url || "").match(/\/items\/(\d+)/)?.[1] || "";

export default function BombaSalsaHomeRail() {
  const { allListings, viatorListings, nbNames } = useExplore();
  const { lang } = useLang();
  const [moreViator, setMoreViator] = useState([]);
  const es = lang === "es";

  useEffect(() => {
    let active = true;
    // The home cache is popularity-limited, so make a small theme-specific
    // query. Keep the original cache as a no-network fallback.
    withNetworkRetry(() => base44.entities.ViatorExperience.filter(
      { status: "Published", title: { $regex: "bomba|salsa", $options: "i" } },
      { sort: "-review_count", limit: 40 }
    )).then((result) => {
      if (active) setMoreViator((result?.items || []).map(normalizeViator));
    }).catch(() => { /* Local FareHarbor cards still render. */ });
    return () => { active = false; };
  }, []);

  const items = useMemo(() => {
    const harbor = new Map(
      (allListings || [])
        .filter((item) => item?.affiliate_provider === "FareHarbor" && item.status === "Published" &&
          /fareharbor\.com\/embeds\/book\//i.test(item.booking_url || ""))
        .map((item) => [fareHarborId(item), item])
    );
    const viator = new Map(
      [...(viatorListings || []), ...moreViator]
        .filter((item) => item?.id && item?.booking_url && item?.type === "Experience")
        .map((item) => [item.id, item])
    );
    return PICKS.map(({ provider, id, rhythm, en, es: descriptionEs }) => {
      const listing = provider === "fareharbor" ? harbor.get(id) : viator.get(id);
      if (!listing) return null;
      return {
        ...toCard(listing, nbNames),
        label: rhythm,
        short_description: en,
        short_description_es: descriptionEs,
      };
    }).filter(Boolean);
  }, [allListings, viatorListings, moreViator, nbNames]);

  if (!items.length) return null;
  return (
    <section id="home-bomba-salsa" aria-label={es ? "Experiencias de bomba y salsa" : "Bomba and salsa experiences"} className="mt-5 scroll-mt-[120px] border-y border-[#e7d4c9] bg-[linear-gradient(105deg,#fff1e7_0%,#fff9f3_52%,#fff0f0_100%)] pb-5 pt-1 dark:border-white/15 dark:bg-[linear-gradient(105deg,#312321_0%,#241e23_52%,#301f27_100%)] sm:mt-8 sm:pb-7">
      <div aria-hidden="true" className="mx-auto h-[3px] max-w-[1240px] bg-[linear-gradient(90deg,#c83b37_0%,#e4aa37_47%,#b64b75_100%)]" />
      <CarouselRail
        editorial
        masthead={es ? "CULTURA VIVA / PUERTO RICO" : "LIVING CULTURE / PUERTO RICO"}
        title={es ? "BOMBA & SALSA." : "BOMBA & SALSA."}
        subtitle={es
          ? "Tambores, movimiento y cultura viva. Descubre experiencias de bomba y salsa en Puerto Rico."
          : "Live drums, movement and living culture. Explore bomba and salsa experiences across Puerto Rico."}
        itemWidth="w-[min(67vw,260px)] min-w-[200px] sm:w-[250px] sm:min-w-0"
      >
        {items.map((item) => <ExperienceCard key={item.id} item={item} />)}
      </CarouselRail>
    </section>
  );
}